import React, {
  useState,
  useRef,
  CSSProperties,
  useMemo,
  useEffect,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CellStyleModule,
  FiltersToolPanelModule,
  SetFilterModule,
  ServerSideRowModelModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  DateFilterModule,
  GridReadyEvent,
  ModuleRegistry,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
} from "ag-grid-community";
import {
  CustomNoRowsOverlay,
  DEFAULT_COL_DEF,
  DEFAULT_GRID_OPTIONS,
  noRowsOverlayComponentParams,
} from "../../gridUtils/gridUtils.js";
import { AppDispatch, RootState } from "../../redux/store.js";
import { ColDef } from "ag-grid-community";
import useAgGridTheme from "../../gridUtils/agGridTheme.js";
import { faEllipsisV, faShare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
ModuleRegistry.registerModules([
  RowSelectionModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellStyleModule,
  SetFilterModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ValidationModule,
  ServerSideRowModelModule,
]);
import InputPopUp from "../../components/InputPopUp";
import { InputPopupField } from "../../components/InputPopUp";
import {
  deleteQuery,
  fetchSavedQueries,
  saveGroupId,
  saveQueryId,
  setSavedQueryName,
  updateQuery,
  nameExists,
  queryExists,
  fetchSharedQueries,
  fetchLegacyTipNetQueries,
  fetchLegacyTipNetQueryDetail,
  setPendingLegacyQuery,
  saveQueryIds,
} from "../../redux/saveSlice.js";
import ConfirmPopup from "../../components/ConfirmPopup.js";
import {
  copyReportToFolder,
  deleteReportFromFolder,
  fetchFolderReports,
} from "../../redux/folderSlice.js";
import { showNotification } from "../../redux/notificationSlice.js";
import ShareQueryPopup from "./ShareQueryPopup.js";
import { useModal } from "../../components/Modal";
import { useNavigate } from "react-router-dom";
import { setSelectedGroupId } from "../../redux/shareSlice.js";
import SubscriptionModel from "./SubscriptionModel.js";
import {
  setCurrentGroupId,
  setCurrentQueryId,
  setCurrentQueryName,
} from "../../redux/subscribeSlice.js";
import {
  resetQuerySlice,
  setLoadQueryIds,
} from "../../redux/queryBuilderSlice.js";
import { resetMapSlice } from "../../redux/mapSlice.js";
import { resetMetaState } from "../../redux/queryMetaSlice.js";
import LoadingOverlay from "../../components/LoadingOverlay.js";
import { parseLegacyTipNetQuery } from "../../utils/legacyTipNetParser";

export function SavedQueriesList() {
  const fleetGridRef = useRef<AgGridReact<String[]>>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const selectedFolder = useSelector(
    (state: RootState) => state.folder.selectedFolder,
  );
  const currentQueryId = useSelector((state: RootState) => state.save.groupId);
  const folderReports = useSelector(
    (state: RootState) => state.folder.folderResports as Record<number, any[]>,
  );
  const [style, setStyle] = useState<CSSProperties>({ height: "550px" });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const queries = useSelector((state: RootState) => state.save.savedQueries);
  const sharedQueries = useSelector(
    (state: RootState) => state.save.sharedQueries,
  );
  const currentUser = useSelector((state: RootState) => state.user.user);
  const [folderQueries, setFolderQueries] = useState<any[]>(queries);
  const [contextMenuStyle, setContextMenuStyle] =
    useState<CSSProperties | null>(null);
  const [contextMenuData, setContextMenuData] = useState<any>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [pendingPosition, setPendingPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (menuRef.current && pendingPosition) {
      const rect = menuRef.current.getBoundingClientRect();
      const menuWidth = rect.width;
      const menuHeight = rect.height;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = pendingPosition.left;
      let top = pendingPosition.top;

      if (left + menuWidth > viewportWidth - 60) {
        left = viewportWidth - menuWidth - 60;
      }

      if (top + menuHeight > viewportHeight - 8) {
        top = viewportHeight - menuHeight - 8;
      }

      setContextMenuStyle((prev: any) => ({
        ...prev,
        left: `${left}px`,
        top: `${top}px`,
      }));
      setPendingPosition(null);
    }
  }, [menuRef.current, pendingPosition]);

  useEffect(() => {
    if (contextMenuStyle && contextMenuStyle.opacity !== 1) {
      setContextMenuStyle((prev) => ({ ...prev, opacity: 1 }));
    }
  }, [contextMenuStyle]);

  function formatDate(date: any) {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  interface CustomColDef extends ColDef {
    cellRenderer?: React.ElementType;
  }

  const fleetGridColumn: CustomColDef[] = useMemo(() => {
    if (selectedFolder.id === -4) {
      return [
        {
          headerName: "Name",
          field: "saved_desc",
          headerTooltip: "Name",
          tooltipField: "saved_desc",
          filter: true,
          sortable: true,
          cellClass: "hyperlink-cell",
          headerClass: "ag-left-header",
          flex: 3,
        },
        {
          headerName: "Source Type",
          field: "source_type",
          headerTooltip: "Source Type",
          tooltipField: "source_type",
          filter: true,
          sortable: true,
          headerClass: "ag-left-header",
          flex: 3,
        },
        {
          headerName: "Folder",
          field: "folder_name",
          headerTooltip: "Folder",
          tooltipField: "folder_name",
          filter: true,
          sortable: true,
          headerClass: "ag-left-header",
          flex: 2,
        },
      ];
    }
    const columns: CustomColDef[] = [
      {
        headerName: "Name",
        field: "name",
        headerTooltip: "Name",
        tooltipField: "name",
        filter: true,
        sortable: true,
        cellClass: "hyperlink-cell",
        headerClass: "ag-left-header",

        width: 250,
        flex: 3,
        cellRenderer: (params) => {
          if (params.data.shared === "Y") {
            return (
              <span>
                <FontAwesomeIcon
                  icon={faShare}
                  style={{
                    fontSize: "16px",
                    color: "#000",
                    marginRight: "10px",
                  }}
                />
                {params.value}
              </span>
            );
          }
          return params.value;
        },
      },
      {
        headerName: "Description",
        field: "description",
        headerTooltip: "Description",
        cellClass: "text-left",
        headerClass: "ag-left-header",
        tooltipField: "description",
        filter: true,
        sortable: true,
        width: 280,
        flex: 3,
      },
      {
        headerName: "Created By",
        field: "owner",
        headerTooltip: "Created By",
        tooltipField: "owner",
        headerClass: "ag-left-header",

        filter: true,

        sortable: true,
        width: 200,
        flex: 2,
      },
      {
        headerName: "Created Date",
        headerTooltip: "Created Date",
        cellClass: "text-center",
        field: "createdDate",
        filter: true,
        sortable: true,
        width: 200,
        flex: 1.5,

        valueGetter: (params: any) => {
          return params.data?.createdDate
            ? formatDate(new Date(params.data.createdDate))
            : null;
        },

        valueFormatter: (params: any) => {
          return params.value ? formatDate(new Date(params.value)) : "";
        },

        tooltipValueGetter: (params: any) => {
          return params.value ? formatDate(new Date(params.value)) : "";
        },
      },
    ];
    if (selectedFolder.id !== -2) {
      columns.push({
        headerName: "",
        field: "actions",
        cellClass: "text-center",
        filter: false,
        sortable: false,
        width: 50,
        flex: 0.5,
        cellRenderer: () => {
          return (
            <span>
              <FontAwesomeIcon
                icon={faEllipsisV}
                style={{ fontSize: "16px", color: "#000" }}
              />
            </span>
          );
        },
      });
    }
    return columns;
  }, [selectedFolder.id]);

  const handleOutsideClick = (event: MouseEvent) => {
    const contextMenuElement = document.getElementById("context-menu");
    if (
      contextMenuElement &&
      !contextMenuElement.contains(event.target as Node)
    ) {
      setContextMenuStyle(null); // Hide the context menu
    }
  };

  const handleCellClick = (params: any) => {
    if (params.colDef.field === "actions") {
      const cellRect = params.event.target.getBoundingClientRect();
      const initialLeft = cellRect.right + 4;
      const initialTop = cellRect.top;

      setContextMenuStyle({
        left: `${initialLeft}px`,
        top: `${initialTop}px`,
        position: "fixed",
        zIndex: 1000,
        width: "170px",
        background: "#fff",
        border: "1px solid #ccc",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
        padding: "10px",
        opacity: 0,
      });
      setPendingPosition({ left: initialLeft, top: initialTop });
      setContextMenuData(params.data);
      document.addEventListener("mousedown", handleOutsideClick);
    } else if (params.colDef.field === "saved_desc") {
      // Legacy TipNet query — fetch detail, parse, then open in Analysis tab
      dispatch(
        fetchLegacyTipNetQueryDetail({
          corp: currentUser.corp.corpCode,
          email: currentUser.name,
          savedId: params.data.saved_id,
        }),
      ).then((result: any) => {
        if (result.payload) {
          const parsed = parseLegacyTipNetQuery(
            result.payload,
            params.data.source_type,
            params.data.saved_desc,
          );
          dispatch(setPendingLegacyQuery(parsed));
          dispatch(resetQuerySlice());
          dispatch(resetMapSlice());
          dispatch(resetMetaState());
          navigate(
            `/analysis/query?legacy=true&name=${encodeURIComponent(params.data.saved_desc)}`,
          );
        }
      });
    } else if (params.colDef.field === "name") {
      dispatch(queryExists({ groupId: params.data.groupId })).then((result) => {
        if (result.payload === true) {
          dispatch(resetQuerySlice());
          dispatch(resetMapSlice());
          dispatch(resetMetaState());

          if (params.data && params.data.savedQueryList?.length > 0) {
            dispatch(
              setLoadQueryIds(
                params.data.savedQueryList.map((query: any) => {
                  return {
                    queryId: query.queryId,
                    cubeName: query.cubeName,
                    tabName: query.name || null,
                  };
                }),
              ),
            );
            navigate(
              `/analysis/query?id=${params.data.groupId}&name=${params.data.name}&owner=${params.data.owner}`,
            );
          }
        } else {
          dispatch(
            showNotification({
              message: `Query ${params.data.name} does not exist anymore`,
              duration: 3000,
              isError: true,
            }),
          );
          dispatch(fetchSharedQueries());
        }
      });
    } else {
      setContextMenuStyle(null);
    }
  };

  interface PopUpOptions {
    title: string;
    saveAction: (
      groupId: number,
      title: string,
      fields: InputPopupField[],
      folderReports: Record<number, any[]>,
      queryList: any[],
    ) => void;
    closeAction: () => void;
  }

  const [showPopup, setShowPopup] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const openSubscriptionModel = useModal(
    <SubscriptionModel mode="create" />,
    "subscriptionModel",
  );
  const [popUpOptions, setPopUpOptions] = useState<PopUpOptions>({
    title: "",
    saveAction: (groupId, title, fields, folderReportsMap, queryList) => {
      if (title === "Rename Query") {
        setIsRenaming(true);
        dispatch(
          nameExists({
            groupId: groupId,
            form: { newname: fields[0].value.trim() },
          }),
        ).then((result) => {
          if (result.payload) {
            setIsRenaming(false);
            dispatch(
              showNotification({
                message: `Query name already exists`,
                duration: 3000,
                isError: true,
              }),
            );
            fields[0].errorMessage = "Query name already exists";
            setPopUpFields(fields);
            return;
          } else {
            const form = {
              newname: fields[0].value.trim(),
              description: fields[1].value,
            };
            dispatch(updateQuery({ groupId: groupId, form: form })).then(() => {
              dispatch(fetchSavedQueries());
              setIsRenaming(false);
              dispatch(
                showNotification({
                  message: `Query renamed successfully`,
                  duration: 3000,
                }),
              );
              setShowPopup(false);
            });
          }
        });
      } else if (title === "Move Query") {
        setIsMoving(true);
        const selectedFolderId =
          fields[0].options?.find(
            (folder: any) => folder.value === fields[0].value,
          )?.value || fields[0].options?.[0].value;
        const existingFolderId = Object.keys(folderReportsMap).find((key) => {
          return folderReportsMap[Number(key)]?.includes(groupId);
        });
        if (selectedFolderId === existingFolderId) {
          setIsMoving(false);
          dispatch(
            showNotification({
              message: `Query already exists in the selected folder`,
              duration: 3000,
              isError: true,
            }),
          );
          setShowPopup(false);
          return;
        }
        dispatch(
          copyReportToFolder({
            folderId: selectedFolderId ?? "-3",
            reportId: groupId,
          }),
        ).then(() => {
          dispatch(
            deleteReportFromFolder({
              folderId: Number(existingFolderId),
              reportId: groupId,
            }),
          ).then(() => {
            dispatch(fetchFolderReports()).then(() => {
              dispatch(fetchSavedQueries());
              const queryName = queryList.find(
                (query: any) => query.groupId === groupId,
              )?.name;
              dispatch(
                showNotification({
                  message: `Query ${queryName} moved successfully`,
                  duration: 3000,
                }),
              );
              setIsMoving(false);

              setShowPopup(false);
            });
          });
        });
      }
    },
    closeAction: () => {
      setShowPopup(false);
    },
  });

  const renameQueryFields = [
    {
      label: "Name",
      type: "text",
      id: "name",
      value: "",
      maxLength: 50,
    },
    {
      label: "Description",
      type: "text",
      id: "description",
      value: "",
      maxLength: 100,
    },
  ];

  const foldersList = useSelector((state: RootState) => state.folder.folders);
  const folderOptions = useMemo(() => {
    if (foldersList[0]?.children) {
      return foldersList[0].children
        .filter((folder: any) => folder.id !== -3)
        .map((folder: any) => ({
          label: String(folder.name),
          value: String(folder.id),
        }));
    }
    return [];
  }, [foldersList]);

  const moveQueryFields = [
    {
      label: "Select Folder",
      type: "select",
      id: "folder",
      options: {},
      value: "", // Add a default value property
    },
  ];

  const [popUpFields, setPopUpFields] = useState<
    { label: string; type: string; id: string; value: string }[]
  >([]);

  const [confirmPopUp, setConfirmPopUp] = useState({
    title: "",
    message: "",
    confirmAction: (title: string, groupId: number) => {},
    closeAction: () => {},
    id: 0,
  });

  const openShareModal = useModal(<ShareQueryPopup />);

  const handleContextMenuAction = (action: string) => {
    dispatch(setSelectedGroupId(contextMenuData.groupId));
    setContextMenuStyle(null);
    document.removeEventListener("mousedown", handleOutsideClick);
    if (action === "Rename") {
      popUpOptions.title = "Rename Query";
      renameQueryFields[0].value = contextMenuData.name ?? "";
      renameQueryFields[1].value = contextMenuData.description ?? "";
      setPopUpFields(renameQueryFields);
      setShowPopup(true);
      dispatch(setSavedQueryName({ name: contextMenuData.name, index: 0 }));
    } else if (action === "Delete") {
      setShowConfirmPopup(true);
      setConfirmPopUp({
        title: "Delete Query",
        message: `Are you sure you want to delete ${contextMenuData.name}?`,
        confirmAction: (title: string, groupId: number) => {
          setIsDeleting(true);
          const form = {
            id: contextMenuData.groupId,
            isGroup: true,
          };
          dispatch(deleteQuery({ form }))
            .then(() => {
              dispatch(fetchSavedQueries());
              if (currentQueryId == form.id) {
                dispatch(setSavedQueryName({ name: "New Query", index: 0 }));
                dispatch(saveQueryId(null));
                dispatch(saveQueryIds([]));
                dispatch(saveGroupId(null));
              }
              setIsDeleting(false);
              dispatch(
                showNotification({
                  message: `Query ${contextMenuData.name} deleted successfully`,
                  duration: 3000,
                }),
              );
            })
            .catch((err) => {
              console.error(err);
              setIsDeleting(false);
            });
          setShowConfirmPopup(false);
        },
        closeAction: () => {
          setShowConfirmPopup(false);
        },
        id: contextMenuData.groupId ?? 0,
      });
    } else if (action === "Remove") {
      setShowConfirmPopup(true);
      setConfirmPopUp({
        title: "Remove Query",
        message: `Are you sure you want to remove query from ${selectedFolder.name}?`,
        confirmAction: (title: string, id: number) => {
          setIsRemoving(true);
          dispatch(
            deleteReportFromFolder({
              folderId: selectedFolder.id,
              reportId: contextMenuData.groupId,
            }),
          )
            .then(() => {
              dispatch(fetchFolderReports());
              dispatch(fetchSavedQueries());
              setIsRemoving(false);

              dispatch(
                showNotification({
                  message: `Query ${contextMenuData.name} removed successfully`,
                  duration: 3000,
                }),
              );
            })
            .catch((err) => {
              setIsRemoving(false);
              console.error(err);
            });
          setShowConfirmPopup(false);
        },
        closeAction: () => {
          setShowConfirmPopup(false);
        },
        id: contextMenuData.groupId ?? 0,
      });
    } else if (action === "Move") {
      popUpOptions.title = "Move Query";
      moveQueryFields[0].options = folderOptions;
      moveQueryFields[0].value =
        folderOptions.length === 1
          ? folderOptions[0].value
          : String(selectedFolder.id);
      setPopUpFields(moveQueryFields);
      setShowPopup(true);
    } else if (action === "Share") {
      openShareModal();
    }
  };

  const updateHeight = () => {
    const newHeight = window.innerHeight - 225;
    setStyle({ height: `${newHeight}px` });
  };

  useEffect(() => {
    fleetGridRef.current?.api?.setFilterModel(null);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    if (selectedFolder.id === -2) {
      // Shared Queries
      dispatch(fetchSharedQueries()).then((result) => {
        setFolderQueries(result.payload);
      });
    } else if (selectedFolder.id === -4) {
      // Legacy TipNet Queries
      dispatch(
        fetchLegacyTipNetQueries({
          corp: currentUser.corp.corpCode,
          email: currentUser.name,
        }),
      ).then((result: any) => {
        setFolderQueries(result.payload);
      });
    } else if (selectedFolder.id === -1) {
      setFolderQueries(queries);
    } else if (selectedFolder.id === -3) {
      // Default folder
      // find all queries that are not assocaited with any folder using folderReports
      const otherFolderQueries = queries.filter((query: any) => {
        // Check if the query's groupId is not present in any folder
        return !Object.keys(folderReports).some((key) => {
          return folderReports[Number(key)]?.includes(query.groupId);
        });
      });
      setFolderQueries(otherFolderQueries);
    } else {
      const folderQueries: number[] = folderReports[selectedFolder.id];
      if (folderQueries) {
        const filteredQueries = queries.filter((query: any) =>
          folderQueries.find(
            (folderQuery: any) => folderQuery === query.groupId,
          ),
        );
        setFolderQueries(filteredQueries);
      } else {
        setFolderQueries([]);
      }
    }
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [selectedFolder, queries]);

  return (
    <div className="grid-row">
      <div>
        <div className="ag-theme-alpine grid-wrapper saved-queries">
          <div style={style}>
            <AgGridReact<String[]>
              theme={useAgGridTheme(null)}
              ref={fleetGridRef}
              rowData={folderQueries}
              defaultColDef={DEFAULT_COL_DEF}
              gridOptions={DEFAULT_GRID_OPTIONS}
              columnDefs={fleetGridColumn as any}
              onCellClicked={handleCellClick}
              suppressContextMenu={true}
              noRowsOverlayComponent={CustomNoRowsOverlay}
              noRowsOverlayComponentParams={noRowsOverlayComponentParams}
            />
          </div>
        </div>
      </div>
      {contextMenuStyle && (
        <div id="context-menu" ref={menuRef} style={contextMenuStyle}>
          <div
            className="context-div"
            onClick={() => handleContextMenuAction("Delete")}
          >
            Delete Permanently
          </div>
          {selectedFolder.id >= 0 && (
            <div
              className="context-div"
              onClick={() => handleContextMenuAction("Remove")}
            >
              Remove from Folder
            </div>
          )}
          <div
            className="context-div"
            onClick={() => handleContextMenuAction("Share")}
          >
            Share / Unshare Query
          </div>
          {folderOptions.length > 0 && selectedFolder.id !== -1 && (
            <div
              className="context-div"
              onClick={() => handleContextMenuAction("Move")}
            >
              Move Item
            </div>
          )}
          <div
            className="context-div"
            onClick={() => handleContextMenuAction("Rename")}
          >
            Rename Query
          </div>
          {/* <div
            className="context-div"
            onClick={() => {
              setContextMenuStyle(null);
              dispatch(setCurrentGroupId(contextMenuData?.groupId || null));
              dispatch(
                setCurrentQueryId(
                  contextMenuData?.savedQueryList[0].queryId || null,
                ),
              );
              dispatch(setCurrentQueryName(contextMenuData?.name || null));
              openSubscriptionModel();
            }}
          >
            Subscribe
          </div> */}
        </div>
      )}
      {showPopup && (
        <InputPopUp
          saveAction={() =>
            popUpOptions.saveAction(
              contextMenuData.groupId,
              popUpOptions.title,
              popUpFields,
              folderReports,
              queries,
            )
          }
          enableLoading={true}
          isLoading={isRenaming || isMoving}
          fields={popUpFields}
          title={popUpOptions.title}
          closeAction={popUpOptions.closeAction}
        ></InputPopUp>
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          title={confirmPopUp.title}
          id={confirmPopUp.id}
          message={confirmPopUp.message}
          confirmAction={confirmPopUp.confirmAction}
          closeAction={confirmPopUp.closeAction}
        ></ConfirmPopup>
      )}
      <LoadingOverlay show={isDeleting || isRemoving} />
    </div>
  );
}
